import api from 'api/axiosInstance';
import apiWithWorkspaceHeader, {
  AxiosMethod,
} from 'utils/apiWithWorkspaceHeader';
import type {
  IWorkspace,
  GetWorkspaceInvitationsResponse,
  ClientInvitation,
} from 'types/workspace.types';
import {
  WorkspaceMemberRole,
  type InviteMembersRequest,
  type InviteMembersResponse,
  type InvitationStatus,
} from 'types/organization.types';
import type { PagingV2Response } from 'types/pagination.types';
import type {
  AvailableCountries,
  IUser,
  UserPlanType,
  VATTypeEnum,
} from 'types/users.types';

const basePath = '/v3/access/workspaces';

export type WorkspaceCreateBody = {
  name: string;
  email: string;
  country: string;
  language: string;
  address?: {
    street?: string;
    postal_code?: string;
    city?: string;
  };
  phone_number?: string;
  itaa_number?: string;
  vat_number?: string;
};

export type WorkspaceInvitationResponse = {
  ok: boolean;
  message: string;
  invitation: {
    id: string;
    email: string;
    role: string;
    workspace_id: string;
    token: string;
    created_at: string;
  };
};

// POST /api/v3/access/workspaces
export const createWorkspace = (
  body: WorkspaceCreateBody,
): Promise<IWorkspace> => api.post(basePath, body);

// PATCH /api/v3/access/workspaces
export const updateWorkspace = (
  body: Partial<WorkspaceCreateBody>,
): Promise<IWorkspace> =>
  apiWithWorkspaceHeader(AxiosMethod.PATCH, basePath, body);

export interface CustomerInvitationFormItem {
  email: string;
  team: string;
}

export interface CustomerInvitationRequestBody {
  email: string;
}

export interface BulkCustomerInvitationRequestBody {
  emails: string[];
}

// POST /api/v3/access/workspaces/client-invites
export const sendAccountantInvitationToClient = (
  invite: BulkCustomerInvitationRequestBody,
): Promise<string> =>
  apiWithWorkspaceHeader(
    AxiosMethod.POST,
    `${basePath}/client-invites`,
    invite,
  );

export interface GetClientInvitationsParams {
  status?: InvitationStatus[];
  page?: number;
  per_page?: number;
}

export interface GetClientInvitationsResponse {
  invitations: ClientInvitation[];
  paging: PagingV2Response;
}

// GET /api/v3/access/workspaces/client-invites
export const getClientInvitations = (
  params?: GetClientInvitationsParams,
): Promise<GetClientInvitationsResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.status) {
    params.status.forEach((status) => searchParams.append('status', status));
  }
  if (params?.page) {
    searchParams.set('page', params.page.toString());
  }
  if (params?.per_page) {
    searchParams.set('per_page', params.per_page.toString());
  }

  const queryString = searchParams.toString();
  const url = `${basePath}/client-invites${
    queryString ? `?${queryString}` : ''
  }`;

  return apiWithWorkspaceHeader(AxiosMethod.GET, url);
};

// DELETE /api/v3/access/workspaces/clients/:userId
export const revokeClientAccess = (
  userId: string,
): Promise<{ success: boolean }> =>
  apiWithWorkspaceHeader(AxiosMethod.DELETE, `${basePath}/clients/${userId}`);

// DELETE /api/v3/access/workspaces/client-invites/:invId
export const revokeClientInvite = (
  invitationId: string,
): Promise<{ success: boolean }> =>
  apiWithWorkspaceHeader(
    AxiosMethod.DELETE,
    `${basePath}/client-invites/${invitationId}`,
  );

type ClientsListParams = {
  page: number;
  per_page: number;
  text?: string;
  user_plan_type?: UserPlanType[];
  peppol_status?: string;
  user_country?: AvailableCountries;
  user_vat_type?: Exclude<VATTypeEnum, VATTypeEnum.exonerated>[];
};

type ClientsListResponse = {
  data: IUser[];
  paging: PagingV2Response;
};

// GET /v3/access/workspaces/clients
// get clients/customers of the workspace
export const getClients = (
  params: ClientsListParams,
): Promise<ClientsListResponse> => {
  const { text, ...restParams } = params;
  return apiWithWorkspaceHeader(
    AxiosMethod.GET,
    `/v3/access/workspaces/clients`,
    {
      params: {
        ...restParams,
        ...(params.user_plan_type && {
          user_plan_type: params.user_plan_type.join(','),
        }),
        ...(params.user_vat_type && {
          user_vat_type: params.user_vat_type.join(','),
        }),
        ...(text && { keyword: text }),
      },
    },
  );
};

// GET /api/v3/access/workspaces/workspace-invites
export const getSentWorkspaceInvitations = (params?: {
  page?: number;
  per_page?: number;
}): Promise<GetWorkspaceInvitationsResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.page) {
    searchParams.set('page', params.page.toString());
  }
  if (params?.per_page) {
    searchParams.set('per_page', params.per_page.toString());
  }

  const queryString = searchParams.toString();
  const url = `${basePath}/workspace-invites${
    queryString ? `?${queryString}` : ''
  }`;

  return apiWithWorkspaceHeader(AxiosMethod.GET, url);
};

// POST /api/v3/access/workspaces/workspace-invites
export const inviteWorkspaceMembers = (
  data: InviteMembersRequest,
): Promise<InviteMembersResponse> =>
  apiWithWorkspaceHeader(AxiosMethod.POST, `${basePath}/workspace-invites`, {
    email: data.email,
    // use when testing locally
    //redirect_uri: 'https://next.experts.accountable.eu/',
    redirect_uri: `${window.location.origin}`,
    role: data?.role || WorkspaceMemberRole.MEMBER,
  });

export const revokeWorkspaceSentInvitation = (
  invitationId: string,
): Promise<{ success: boolean }> =>
  apiWithWorkspaceHeader(
    AxiosMethod.DELETE,
    `${basePath}/workspace-invites/${invitationId}`,
  );

// DELETE /api/v3/access/workspaces
export const deleteWorkspace = (
  workspaceId: string,
): Promise<{ success: boolean }> =>
  apiWithWorkspaceHeader(AxiosMethod.DELETE, `${basePath}/${workspaceId}`);

// DELETE /api/v3/access/workspaces/members/:memberId
export const removeWorkspaceMember = (
  memberId: string,
): Promise<{ success: boolean }> =>
  apiWithWorkspaceHeader(AxiosMethod.DELETE, `${basePath}/members/${memberId}`);

// PATCH /api/v3/access/workspaces/members/:memberId/role
export const updateWorkspaceMemberRole = (
  memberId: string,
  role: WorkspaceMemberRole,
): Promise<{ success: boolean }> =>
  apiWithWorkspaceHeader(
    AxiosMethod.PATCH,
    `${basePath}/members/${memberId}/role`,
    {
      role,
    },
  );
